<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="ai-sheet"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="titleId"
      @pointerup.self="$emit('cancel')"
    >
      <div
        class="ai-sheet-panel"
        @pointerdown.stop
        @pointerup.stop
      >
        <h3 :id="titleId">{{ t('home.vsAiPick') }}</h3>
        <fieldset class="ai-levels">
          <legend class="visually-hidden">{{ t('settings.vsAiDifficulty') }}</legend>
          <button
            v-for="level in AI_DECK_DIFFICULTIES"
            :key="level"
            type="button"
            class="ai-level"
            :class="{ active: duelPrefs.vsAiDifficulty === level }"
            :aria-pressed="duelPrefs.vsAiDifficulty === level"
            @click="duelPrefs.setVsAiDifficulty(level)"
          >
            {{ t(`settings.vsAiLevels.${level}`) }}
          </button>
        </fieldset>
        <p v-if="duelPrefs.vsAiDifficulty === 'test'" class="ai-test-note">
          {{ t('settings.vsAiTestHint') }}
        </p>
        <div class="ai-actions">
          <button type="button" class="ai-btn ghost" @click="$emit('cancel')">
            {{ t('home.vsAiCancel') }}
          </button>
          <button type="button" class="ai-btn fill" @click="$emit('start')">
            {{ t('home.vsAiStart') }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { AI_DECK_DIFFICULTIES } from '@shared/index'
import { useDuelPrefsStore } from '../stores/duelPrefs'
import { useI18n } from '../i18n/t'

defineProps<{ visible: boolean }>()
defineEmits<{ cancel: []; start: [] }>()

const { t } = useI18n()
const duelPrefs = useDuelPrefsStore()
const titleId = 'vs-ai-start-title'
</script>

<style scoped>
.ai-sheet {
  position: fixed;
  inset: 0;
  z-index: 400;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(4, 6, 16, 0.88);
}

.ai-sheet-panel {
  width: min(420px, 100%);
  padding: 16px;
  border-radius: 16px;
  background: #1f1f23;
  color: #e5e1e6;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.3), 0 1px 3px 1px rgba(0, 0, 0, 0.15);
}

.ai-sheet-panel h3 {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 500;
}

.ai-test-note {
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.4;
  color: var(--md-sys-color-on-surface-variant, #c7c4d0);
}

.ai-levels {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0 0 12px;
  padding: 0;
  border: 0;
}

.ai-level {
  min-height: 36px;
  padding: 0 14px;
  border: 1px solid var(--md-sys-color-outline, #90909a);
  border-radius: 18px;
  background: transparent;
  color: inherit;
  font: inherit;
  font-size: 14px;
  cursor: pointer;
}

.ai-level.active {
  border-color: transparent;
  background: var(--md-sys-color-primary-container, #6e5c1a);
  color: var(--md-sys-color-on-primary-container, #f5f5f5);
}

.ai-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.ai-btn {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 20px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
}

.ai-btn.ghost {
  border: 1px solid var(--md-sys-color-outline, #90909a);
  background: transparent;
  color: inherit;
}

.ai-btn.fill {
  border: 0;
  background: var(--md-sys-color-primary, #e8c547);
  color: var(--md-sys-color-on-primary, #1c1b1f);
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
</style>
